import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/**
 * Why this change is being made — asked on every audited product action.
 *
 * One control rather than a reason input per dialog: every product action
 * writes to the same trail, and a reviewer comparing rows should not be
 * reading five different vocabularies because five dialogs each invented
 * their own. The preset list is per action, since the honest reasons for
 * correcting a volume are not the reasons for pooling.
 *
 * "Other" requires free text. A reason of literally "Other" records that
 * something happened for a reason nobody wrote down, which is worse than no
 * reason at all — it looks answered.
 */
export function ReasonField({
  presets,
  reason,
  detail,
  onReasonChange,
  onDetailChange,
  label = 'Reason',
  hint,
}: {
  presets: readonly string[]
  reason: string
  detail: string
  onReasonChange: (value: string) => void
  onDetailChange: (value: string) => void
  label?: string
  hint?: string
}) {
  const needsDetail = reason === 'Other'

  return (
    <div className='space-y-1.5'>
      <p className='text-sm font-medium'>
        {label} <span className='text-destructive'>*</span>
      </p>
      <div className='flex flex-wrap gap-1.5'>
        {presets.map(r => (
          <button
            key={r}
            type='button'
            onClick={() => {
              onReasonChange(r)
              // Leaving "Other" hides the detail box, so anything typed there
              // must go with it — otherwise a field the operator can no longer
              // see keeps shaping what the trail records.
              if (r !== 'Other') onDetailChange('')
            }}
            className={cn(
              'rounded-full border px-3 py-1 text-sm transition-colors',
              reason === r ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent',
            )}
          >
            {r}
          </button>
        ))}
      </div>
      {needsDetail && (
        <Input
          value={detail}
          onChange={e => onDetailChange(e.target.value)}
          placeholder='Describe what happened'
          aria-label='Reason detail'
          autoFocus
        />
      )}
      {hint && !needsDetail && <p className='text-xs text-muted-foreground'>{hint}</p>}
    </div>
  )
}

/** Whether a reason is complete enough to record. */
export function reasonIsComplete(reason: string, detail: string): boolean {
  if (!reason.trim()) return false
  return reason !== 'Other' || detail.trim().length > 0
}

/** The reason, with free text folded in, as the trail should store it. */
export function composeReason(reason: string, detail: string): string {
  return detail.trim() ? `${reason}: ${detail.trim()}` : reason
}

/**
 * Preset reasons per action.
 *
 * Deliberately short lists: a long menu gets answered with whatever is first.
 * These are the reasons that actually recur in this workflow; anything else
 * goes through Other with free text, and a preset that keeps showing up as
 * free text is the signal to add it here.
 */
export const SPLIT_REASONS = [
  'Dose preparation',
  'Aliquot for testing',
  'Storage capacity',
  'Physician request',
  'Other',
] as const

export const POOL_REASONS = [
  'Combine collections',
  'Dose preparation',
  'Physician request',
  'Other',
] as const

export const LABEL_REASONS = [
  'Initial label',
  'Reprint — damaged',
  'Corrected information',
  'Additional message',
  'Other',
] as const

export const STORAGE_REASONS = [
  'Initial placement',
  'Relocation',
  'Capacity',
  'Temperature excursion',
  'Removed for processing',
  'Other',
] as const

export const CREATE_REASONS = [
  'Scheduled collection',
  'Order received',
  'Reprocessing',
  'Other',
] as const

/** Change product code (z8nrz7e72t). */
export const CHANGE_CODE_REASONS = [
  'Processing changed the product',
  'Wrong code at creation',
  'Code inactivated',
  'Other',
] as const
